import React, { useCallback, useEffect } from 'react';

import { useAddEmployeeQuery } from '@entities/myTeam/lib/hooks';
import { keysToCamel } from '@shared/helpers/snakeToCamel';
import { useDialog, useHandleForm } from '@shared/hooks';
import { Checkbox, CustomModal, Form, UiButton } from '@shared/ui';

import { AddEmployeeFields } from './AddEmployeeFields';
import { AddEmployeePopup } from './AddEmployeePopup';
import cls from './AddEmployeeToTeam.module.css';
import { useAddEmployerToTeam } from '../lib/hooks/useAddEmployerToTeam';
import { IFormValues } from '../model/types';

interface IAddEmployerToTeamProps<T = unknown> {
    onCloseCb?: () => void;
    onSuccessCb?: (data?: T) => void;
}

export const AddEmployeeToTeam = <T = unknown>({
    onCloseCb,
    onSuccessCb,
}: IAddEmployerToTeamProps<T>) => {
    const {
        inputRef,
        phoneInputWrapperRef,
        t,
        enableTelephoneNotice,
        handleTelephoneNotice,
        enablePopup,
        handleEnablePopup,
    } = useAddEmployerToTeam();

    const { isOpen, close: handleClose } = useDialog(true, onCloseCb);
    const {isPending, mutateAsync, data: addEmployeeData} = useAddEmployeeQuery();

    const { control, handleSubmit, isSubmitting, errors, reset, setValue, setError } =
        useHandleForm<IFormValues>({onSubmit: async (data) => {
            const { email, phone, estimateUserRole } = data;
            const res = await mutateAsync({
                email: email.trim(),
                position: estimateUserRole.trim(),
                phone,
            });
            if (res?.data?.status === 'success') {
                const keysToCamelRes =  keysToCamel(res?.data?.data);
                if (keysToCamelRes.isPending) {
                    handleEnablePopup();
                } else {
                    onSuccessCb?.();
                    onCloseCb?.();
                    handleClose();
                }
                return;
            }
            console.log('res: ', res);
            if (res?.data?.status === 'error') {
                setError("email", {type: 'custom', message: res?.data.message})
            }
        }, defaultValues: {}, resetEnable: false});

    const enableModal = addEmployeeData?.status != 200;

    const isDisabled =
        isSubmitting ||
        !!errors.email ||
        !!errors.estimateUserRole ||
        (enableTelephoneNotice && !!errors.phone);

    useEffect(() => {
        if (!enableTelephoneNotice) {
            setValue('phone', '');
        }
    }, [enableTelephoneNotice, setValue]);

    const onHideModal = useCallback(() => {
        reset();
        handleClose();
        onCloseCb?.();
    }, [reset, onCloseCb, handleClose]);

    const handleClosePopup = useCallback(() => {
        handleEnablePopup();
        onCloseCb?.();
        onSuccessCb?.();
        handleClose();
    }, [onCloseCb, handleClose, onSuccessCb, handleEnablePopup]);

    return (
        <>
            <AddEmployeePopup isOpen={enablePopup} onHide={handleClosePopup} t={t} />
             {enableModal && (
                <CustomModal
                    isOpen={isOpen}
                    onHideModal={onHideModal}
                    contentClasses={cls.AddEmployerToTeam}
                    closeButtonClass={cls.CloseButton}
                    isLoading={isPending}
                >
                    <CustomModal.Header classes={cls.CustomModalHeader}>
                        {t('AddEmployerToTeam.Modal.Header')}
                    </CustomModal.Header>
                    <CustomModal.Body>
                        <Form onSubmit={handleSubmit} id="add-employer-form" classes={cls.Form}>
                            <AddEmployeeFields
                                control={control}
                                errors={errors}
                                inputRef={inputRef}
                                phoneInputWrapperRef={phoneInputWrapperRef}
                                t={t}
                                enableTelephoneNotice={enableTelephoneNotice}
                            />
                            <Checkbox
                                isActive={enableTelephoneNotice}
                                title={t('AddEmployerToTeam.Modal.TelephoneNotice.Checkbox.label')}
                                onClick={handleTelephoneNotice}
                            />
                        </Form>
                    </CustomModal.Body>
                    <CustomModal.Footer classes={cls.CustomModalFooter}>
                        <UiButton
                            btnColor="secondary"
                            btnText={t('AddEmployerToTeam.Modal.CancelBtn')}
                            onClick={onHideModal}
                        />
                        <UiButton
                            type="submit"
                            onSubmit={handleSubmit}
                            form="add-employer-form"
                            btnColor="primary"
                            btnText={t('AddEmployerToTeam.Modal.SubmitBtn')}
                            disabled={isDisabled}
                        />
                    </CustomModal.Footer>
                </CustomModal>
             )}
        </>
    );
};
